import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import Checkbox from '../../../checkbox';
import TreeSelectItemContent from '../tree-select-item-content';
import { itemCheckStates, INDENT_PX, SELECT_ALL_ID } from '../../constants';
import { useTreeSelectContext } from '../../context/tree-select-context';

const TreeSelectItem = ({ item, depth }) => {
  const {
    activeId,
    checkStateMap,
    selectAllState,
    expandedIds,
    setActiveId,
    onItemToggle,
    onSelectAllToggle,
    onExpandToggle,
  } = useTreeSelectContext();

  const ref = useRef(null);
  const isMouseActive = useRef(false);

  const isActive = activeId === item.id;
  const hasChildren = Boolean(item.items?.length);
  const isExpandable = !item.disabled && hasChildren;
  const isExpanded = hasChildren && expandedIds.has(item.id);
  const isSelectAll = item.id === SELECT_ALL_ID;
  const checkState = isSelectAll
    ? selectAllState
    : (checkStateMap.get(item.id) ?? itemCheckStates.UNCHECKED);
  const isSelected = checkState === itemCheckStates.CHECKED;
  const indeterminate = checkState === itemCheckStates.INDETERMINATE;

  const handleToggle = (isChecked) => {
    if (isSelectAll) {
      onSelectAllToggle(isChecked);
    } else {
      onItemToggle(item, isChecked);
    }
  };

  const handleExpandToggle = () => {
    onExpandToggle(item.id);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    handleToggle(!isSelected);
  };

  const handleMouseEnter = () => {
    isMouseActive.current = true;
    setActiveId(item.id);
  };

  const handleMouseLeave = () => {
    isMouseActive.current = false;
  };

  const classes = classNames('ws-ui-tree-select__item', {
    'ws-ui-tree-select__item--selected': isSelected,
    'ws-ui-tree-select__item--disabled': item.disabled,
    'ws-ui-tree-select__item--active': isActive,
  });

  useEffect(() => {
    if (isActive && !isMouseActive.current) {
      ref.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [isActive]);

  return (
    <div
      ref={ref}
      className={classes}
      role="option"
      aria-selected={isSelected}
      aria-disabled={item.disabled}
      tabIndex={-1}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...(!item.disabled && { onClick: handleClick })}
    >
      <div style={{ paddingLeft: depth * INDENT_PX }}>
        <Checkbox
          id={String(item.id)}
          size="small"
          value={String(item.id)}
          checked={isSelected}
          indeterminate={indeterminate}
          disabled={item.disabled}
          isLabelBold={false}
          label={
            <TreeSelectItemContent
              isExpanded={isExpanded}
              isSelected={isSelected}
              isExpandable={isExpandable}
              label={item.label}
              icon={item.icon}
              flag={item.flag}
              disabled={item.disabled}
              onExpandToggle={handleExpandToggle}
            />
          }
          onChange={handleToggle}
        />
      </div>
    </div>
  );
};

export default React.memo(TreeSelectItem);
